import { useState } from "react"
import { useTranslation } from "react-i18next"
import {
  Combobox,
  ComboboxContent,
  ComboboxEmpty,
  ComboboxInput,
  ComboboxItem,
  ComboboxList,
} from "@/components/ui/combobox"

// Remove accents/diacritics from a string for comparison
function normalizeString(str: string): string {
  return str
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
}

export interface FilterOption {
  id: string
  name: string
  color?: string
}

interface FilterComboboxProps {
  value: string
  onChange: (value: string) => void
  options: FilterOption[]
  placeholder?: string
  emptyLabel?: string
  showColorDot?: boolean
  className?: string
}

export function FilterCombobox({
  value,
  onChange,
  options,
  placeholder,
  emptyLabel,
  showColorDot = false,
  className,
}: FilterComboboxProps) {
  const { t } = useTranslation()
  const [inputValue, setInputValue] = useState("")
  const [open, setOpen] = useState(false)

  // Add "All" option at the beginning
  const allOptions: FilterOption[] = [
    { id: "", name: emptyLabel ?? t("inventory.filters.all") },
    ...options,
  ]

  const selectedOption = allOptions.find((o) => o.id === value)

  // Filter options based on input only when there's actual typed input
  // Uses accent-insensitive comparison
  const filteredOptions = inputValue
    ? allOptions.filter((o) =>
        normalizeString(o.name).includes(normalizeString(inputValue))
      )
    : allOptions

  function handleSelect(option: FilterOption | null) {
    onChange(option?.id ?? "")
    setInputValue("")
    setOpen(false)
  }

  function handleOpenChange(isOpen: boolean) {
    setOpen(isOpen)
    if (isOpen) {
      // Clear input when opening so all options are visible
      setInputValue("")
    }
  }

  return (
    <Combobox
      open={open}
      onOpenChange={handleOpenChange}
      value={selectedOption}
      onValueChange={handleSelect}
      modal={false}
    >
      <ComboboxInput
        placeholder={open ? placeholder : (selectedOption?.name ?? placeholder)}
        className={className}
        value={open ? inputValue : (selectedOption?.name ?? "")}
        onChange={(e) => setInputValue(e.target.value)}
      />
      <ComboboxContent>
        <ComboboxEmpty>{t("inventory.filters.noResults")}</ComboboxEmpty>
        <ComboboxList>
          {filteredOptions.map((option) => (
            <ComboboxItem key={option.id || "_all"} value={option}>
              {showColorDot && option.color && (
                <span
                  className="w-3 h-3 rounded-full shrink-0"
                  style={{ backgroundColor: option.color }}
                />
              )}
              {option.name}
            </ComboboxItem>
          ))}
        </ComboboxList>
      </ComboboxContent>
    </Combobox>
  )
}
